import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Stack,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  CardMedia,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  AccessTime,
  Verified,
  LocalHospital,
  ArrowBack,
  Star,
  MedicalServices,
  People,
  Hotel,
  Badge,
  Map as MapIcon,
  Business,
} from '@mui/icons-material';
import { getServiceProvider, getDoctorsByServiceProvider } from '../services/openapis';
import { config } from '../config';

const HospitalDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [hospital, setHospital] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const doctorSpecialties = useMemo(() => {
    const list = (doctors || [])
      .map((doc) => {
        const specialtyObj = doc?.specialty;
        const name =
          specialtyObj?.name ||
          doc?.specialty_name ||
          doc?.specialtyName ||
          doc?.speciality_name ||
          null;
        if (!name) return null;
        return {
          id: specialtyObj?.id ?? name,
          name,
        };
      })
      .filter(Boolean);

    const map = new Map();
    for (const s of list) {
      const key = String(s.id ?? s.name).toLowerCase();
      if (!map.has(key)) map.set(key, s);
    }

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [doctors]);

  useEffect(() => {
    const fetchHospitalDetails = async () => {
      try {
        setLoading(true);
        console.log('Fetching hospital details for ID:', id);
        
        // Fetch hospital details first
        console.log('Calling getServiceProvider...');
        const hospitalData = await getServiceProvider(id);
        console.log('Hospital data received:', hospitalData);
        setHospital(hospitalData);
        
        // Use doctors from the hospital data if available, otherwise fetch separately
        if (hospitalData.doctors && hospitalData.doctors.length > 0) {
          setDoctors(hospitalData.doctors);
        } else {
          // Then fetch doctors separately if not included
          console.log('Calling getDoctorsByServiceProvider...');
          try {
            const doctorsData = await getDoctorsByServiceProvider(id);
            console.log('Doctors data received:', doctorsData);
            setDoctors(doctorsData.results || []);
          } catch (doctorsError) {
            console.warn('Failed to fetch doctors, but continuing with hospital data:', doctorsError);
            setDoctors([]);
          }
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching hospital details:', err);
        setError(`Failed to fetch hospital details. Error: ${err.message || err}`);
      } finally {
        console.log('Setting loading to false');
        setLoading(false);
      }
    };

    if (id) {
      console.log('Hospital ID found, starting fetch:', id);
      fetchHospitalDetails();
    } else {
      console.error('No hospital ID provided');
      setError('No hospital ID provided in URL');
      setLoading(false);
    }
  }, [id]);

  const handleViewAllDoctors = () => {
    // Preserve any filters that were used on the hospitals list (e.g. specialty, availableDate)
    const incomingFilters = (location.state && location.state.filters) || {};
    
    const params = new URLSearchParams(incomingFilters);
    if (hospital?.id) {
      params.set('provider', hospital.id);
    }
    
    const queryString = params.toString();
    navigate(`/find-doctors${queryString ? `?${queryString}` : ''}`);
  };

  const handleDoctorClick = (doctorId) => {
    navigate(`/doctors/${doctorId}`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Helper function to get hospital rating
  const getHospitalRating = () => {
    if (hospital?.star_rating) return hospital.star_rating;
    return 4.2; // Default rating
  };

  // Helper function to get review count
  const getReviewCount = () => {
    if (hospital?.review_count) return hospital.review_count;
    return Math.floor(Math.random() * 200 + 50);
  };

  // Get filters from navigation state (must be before early returns)
  const incomingFilters = (location.state && location.state.filters) || {};
  
  // Filter doctors based on incoming filters (e.g., specialty)
  // Must be called before any early returns to follow React Hooks rules
  const filteredDoctors = useMemo(() => {
    let filtered = [...doctors];
    
    // Filter by specialty if provided
    if (incomingFilters.specialty) {
      const specialtyFilter = incomingFilters.specialty.toLowerCase();
      filtered = filtered.filter(doctor => {
        const doctorSpecialty = (doctor.specialty_name || '').toLowerCase();
        return doctorSpecialty.includes(specialtyFilter) || specialtyFilter.includes(doctorSpecialty);
      });
    }
    
    // Filter by available date if provided
    if (incomingFilters.availableDate) {
      // Note: This would require checking doctor availability, which may need API call
      // For now, we'll skip date filtering as it requires availability data
    }
    
    return filtered;
  }, [doctors, incomingFilters]);

  // Use filtered doctors for display
  const displayDoctors = filteredDoctors;
  const shouldShowDoctorsSection = displayDoctors.length > 0;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!hospital) {
    return (
      <Container>
        <Alert severity="warning" sx={{ mt: 4 }}>
          Hospital not found.
        </Alert>
      </Container>
    );
  }

  const rating = getHospitalRating();
  const reviewCount = getReviewCount();

  return (
    <Box sx={{ bgcolor: isMobile ? '#f5f5f5' : 'inherit', minHeight: '100vh' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
        {/* Header with Back Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant={isMobile ? "h5" : "h4"} component="h1">
            Hospital Details
          </Typography>
        </Box>

        {/* Mobile Layout */}
        {isMobile ? (
          <Stack spacing={2}>
            {/* Hospital Images */}
            {(hospital.building_image || hospital.nameplate_image) && (
              <Card sx={{ borderRadius: 2 }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={hospital.building_image ? (hospital.building_image.startsWith('http') ? hospital.building_image : `${config.backend.host}${hospital.building_image}`) : (hospital.nameplate_image ? (hospital.nameplate_image.startsWith('http') ? hospital.nameplate_image : `${config.backend.host}${hospital.nameplate_image}`) : '')}
                  alt={hospital.name}
                  sx={{ objectFit: 'cover' }}
                />
              </Card>
            )}

            {/* Basic Info Card */}
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      bgcolor: 'primary.main',
                    }}
                  >
                    <LocalHospital sx={{ fontSize: 30 }} />
                  </Avatar>
                  
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.3rem', color: 'text.primary' }}>
                        {hospital.name}
                      </Typography>
                  
                    </Box>
                    
                    {/* Rating */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Star sx={{ color: '#ffa726', fontSize: 18 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {rating}
                      </Typography>
                     
                    </Box>
                  </Box>
                </Box>

                {/* Key Stats */}
                <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                      {displayDoctors.length}
                    </Typography>
                    <Typography variant="caption" color="text.primary">
                      Doctors
                    </Typography>
                  </Box>
                  {hospital.bed_count && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                        {hospital.bed_count}
                      </Typography>
                      <Typography variant="caption" color="text.primary">
                        Beds
                      </Typography>
                    </Box>
                  )}
                  {/* <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                      24/7
                    </Typography>
                    <Typography variant="caption" color="text.primary">
                      Available
                    </Typography>
                  </Box> */}
                </Box>
              </CardContent>
            </Card>

            {/* Contact Info Card */}
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Contact Information
                </Typography>
                
                <Stack spacing={2}>
                  {/* Complete Address */}
                  {(hospital.address_line1 || hospital.address_line2 || hospital.locality || hospital.city) && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <LocationOn sx={{ color: 'primary.main', mt: 0.5 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>Address</Typography>
                        <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
                          {hospital.address_line1 && `${hospital.address_line1}, `}
                          {hospital.address_line2 && `${hospital.address_line2}, `}
                          {hospital.locality && `${hospital.locality}, `}
                          {hospital.city && `${hospital.city}, `}
                          {hospital.state && `${hospital.state} `}
                          {hospital.pincode && `- ${hospital.pincode}`}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {/* Coordinator 1 Contact */}
                  {hospital.coordinator_1_number && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Phone sx={{ color: 'primary.main' }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {hospital.coordinator_1_name || 'Coordinator'}
                        </Typography>
                        <Typography variant="body2" color="text.primary">{hospital.coordinator_1_number}</Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Coordinator 2 Contact */}
                  {hospital.coordinator_2_number && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Phone sx={{ color: 'primary.main' }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {hospital.coordinator_2_name || 'Coordinator 2'}
                        </Typography>
                        <Typography variant="body2" color="text.primary">{hospital.coordinator_2_number}</Typography>
                      </Box>
                    </Box>
                  )}
                

                  {hospital.location && (
                    <Button
                      variant="outlined"
                      startIcon={<MapIcon />}
                      href={hospital.location}
                      target="_blank"
                      sx={{ mt: 1, textTransform: 'none' }}
                    >
                      View on Map
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Specialties Card */}
            {doctorSpecialties.length > 0 && (
              <Card sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    <MedicalServices sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Specialties
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {doctorSpecialties.map((specialty) => {
                      // Check if this specialty matches the incoming filter
                      const isFilteredSpecialty = incomingFilters.specialty &&
                        (specialty.name.toLowerCase().includes(incomingFilters.specialty.toLowerCase()) ||
                         incomingFilters.specialty.toLowerCase().includes(specialty.name.toLowerCase()));

                      return (
                        <Chip
                          key={specialty.id}
                          label={specialty.name}
                          variant={isFilteredSpecialty ? "filled" : "outlined"}
                          color="primary"
                          size="small"
                          sx={{
                            ...(isFilteredSpecialty && {
                              backgroundColor: 'primary.main',
                              color: 'white',
                              fontWeight: 600,
                            })
                          }}
                        />
                      );
                    })}
                  </Box>
                  {incomingFilters.specialty && (
                    <Typography variant="body2" sx={{ mt: 2, color: 'primary.main', fontWeight: 500 }}>
                     <Button variant="contained" color="primary" onClick={() => handleViewAllDoctors()}>View All</Button>
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Doctors Section - Only show if there are filtered doctors */}
            {shouldShowDoctorsSection && (
              <Card sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      <People sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Our Doctors ({displayDoctors.length})
                    </Typography>
                    {displayDoctors.length > 3 && (
                      <Button
                        variant="text"
                        onClick={handleViewAllDoctors}
                        sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                      >
                        View All
                      </Button>
                    )}
                  </Box>

                  <Stack spacing={2}>
                    {displayDoctors.slice(0, 3).map((doctor) => (
                      <Card
                        key={doctor.id}
                        variant="outlined"
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: 2,
                          },
                        }}
                        onClick={() => handleDoctorClick(doctor.id)}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              src={doctor.profile_photo}
                              alt={doctor.name}
                              sx={{ width: 50, height: 50 }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                Dr. {doctor.name}
                              </Typography>
                              <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.8rem' }}>
                                {doctor.specialty_name} • {doctor.experience_years}y exp
                              </Typography>
                              <Typography variant="body2" color="primary" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                ₹{parseFloat(doctor.consultation_fee).toFixed(0)}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>

                  {displayDoctors.length > 3 && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Button
                        variant="contained"
                        onClick={handleViewAllDoctors}
                        sx={{ textTransform: 'none', borderRadius: 3 }}
                      >
                        View All {displayDoctors.length} Doctors
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Stack>
        ) : (
          /* Desktop Layout */
          <>
            {/* Hospital Information Card with Side-by-Side Layout */}
            <Card sx={{ 
              mb: 4, 
              p: 0, 
              borderRadius: 3, 
              overflow: 'hidden', 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid',
              borderColor: 'primary.light'
            }}>
              <CardContent style={{padding:0}}>
                <Box sx={{ display: 'flex', minHeight: 280 }}>
                  {/* Left Column - Building Image */}
                  {(hospital.building_image || hospital.nameplate_image) && (
                    <Box sx={{ width: '40%', minWidth: 300 }}>
                      <CardMedia
                        component="img"
                        height="100%"
                        image={hospital.building_image ? (hospital.building_image.startsWith('http') ? hospital.building_image : `${config.backend.host}${hospital.building_image}`) : (hospital.nameplate_image ? (hospital.nameplate_image.startsWith('http') ? hospital.nameplate_image : `${config.backend.host}${hospital.nameplate_image}`) : '')}
                        alt={hospital.name}
                        sx={{ 
                          objectFit: 'cover',
                          height: 350,
                          width: '100%'
                        }}
                      />
                    </Box>
                  )}

                  {/* Right Column - Hospital Details */}
                  <Box sx={{ 
                    flex: 1, 
                    p: 3, 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderLeft: '1px solid',
                    borderColor: 'primary.light'
                  }}>
                    {/* Hospital Info Section */}
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3,  }}>
                    
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.2 }}>
                            {hospital.name}
                          </Typography>
                          
                          {/* Rating */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1}}>
                            <Star sx={{ color: '#ffa726', fontSize: 20 }} />
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {rating}
                            </Typography>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                             Doctors - {displayDoctors.length} 
                          </Typography>
                          </Box>

                          {/* Doctor Count */}
                        
                        </Box>
                      </Box>
                    </Box>

                    {/* Contact Info Section */}
                    <Box sx={{ 
                      mt: 3,
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.300'
                    }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Contact Information
                      </Typography>
                      
                      <Stack spacing={1}>
                        {/* Complete Address */}
                        {(hospital.address_line1 || hospital.address_line2 || hospital.locality || hospital.city) && (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <LocationOn sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                              <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1}}>
                                {hospital.address_line1 && `${hospital.address_line1}, `}
                                {hospital.address_line2 && `${hospital.address_line2}, `}
                                {hospital.locality && `${hospital.locality}, `}
                                {hospital.city && `${hospital.city}, `}
                                {hospital.state && `${hospital.state} `}
                                {hospital.pincode && `- ${hospital.pincode}`}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        
                        {/* Coordinator 1 Contact */}
                        {hospital.coordinator_1_number && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Phone sx={{ color: 'primary.main' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {hospital.coordinator_1_name || 'Coordinator'}
                              </Typography>
                              <Typography variant="body2" color="text.primary">{hospital.coordinator_1_number}</Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Coordinator 2 Contact */}
                        {hospital.coordinator_2_number && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Phone sx={{ color: 'primary.main' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {hospital.coordinator_2_name || 'Coordinator 2'}
                              </Typography>
                              <Typography variant="body2" color="text.primary">{hospital.coordinator_2_number}</Typography>
                            </Box>
                          </Box>
                        )}
                        
                        {/* License Information */}
                    

                        {/* Hospital Type */}
                     
                    

                        {hospital.location && (
                          <Button
                            variant="outlined"
                            startIcon={<MapIcon />}
                            href={hospital.location}
                            target="_blank"
                            sx={{ mt: 1, textTransform: 'none' }}
                          >
                            View on Map
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Specialties Section */}
            {hospital.specialties && hospital.specialties.length > 0 && (
              <Card sx={{ 
              mb: 4, 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid',
              borderColor: 'grey.600'
            }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    <MedicalServices sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Specialties
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {hospital.specialties.map((specialty, index) => (
                      <Chip
                        key={index}
                        label={specialty.name}
                        variant="outlined"
                        color="primary"
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Doctors Section - Only show if there are filtered doctors */}
            {shouldShowDoctorsSection && (
              <Card sx={{ 
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                border: '1px solid',
                borderColor: 'grey.300'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      <People sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Our Doctors ({displayDoctors.length})
                    </Typography>
                    {displayDoctors.length > 6 && (
                      <Button
                        variant="outlined"
                        onClick={handleViewAllDoctors}
                        sx={{ textTransform: 'none' }}
                      >
                        View All Doctors
                      </Button>
                    )}
                  </Box>

                  <Grid container spacing={3}>
                    {displayDoctors.slice(0, 6).map((doctor) => (
                      <Grid item xs={12} sm={6} md={4} key={doctor.id}>
                        <Card
                          sx={{
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: 'grey.600',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                            },
                          }}
                          onClick={() => handleDoctorClick(doctor.id)}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Avatar
                                src={doctor.profile_photo}
                                alt={doctor.name}
                                sx={{ width: 50, height: 50, mr: 2 }}
                              />
                              <Box>
                                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                                  Dr. {doctor.name}
                                </Typography>
                                <Typography variant="body2" color="text.primary">
                                  {doctor.specialty_name}
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                              {doctor.qualifications?.name || doctor.qualifications?.abbreviation || 'N/A'} • {doctor.experience_years} years experience
                            </Typography>
                            
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                              Consultation: ₹{parseFloat(doctor.consultation_fee).toFixed(0)}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  {displayDoctors.length > 6 && (
                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                      <Button
                        variant="contained"
                        onClick={handleViewAllDoctors}
                        sx={{ textTransform: 'none', px: 4 }}
                      >
                        View All {displayDoctors.length} Doctors
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default HospitalDetails; 